import Button, { ButtonSizeEnum, ButtonTypeEnum } from "@/components/atoms/Button";
import {
  NavV2Item,
  NavV2MenuColumn,
  NavV2MenuItem,
  navbarV2Data,
} from "@/shared/data/navbar.v2.data";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faBookOpen,
  faBrain,
  faBrowsers,
  faCalendar,
  faCirclePlay,
  faFile,
  faGlobe,
  faInfinity,
  faLink,
  faMegaphone,
  faMobileScreen,
  faMoneyBill1,
  faScreenUsers,
  faShield,
  faUserHeadset,
  faUsers,
  faVideo,
  faWandMagicSparkles,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

/** Map the short `icon` key used in navbar.v2.data.ts to a concrete
 *  FontAwesome Pro Regular IconDefinition. Keeping the lookup table in
 *  this component (not in the data layer) lets the data file stay free
 *  of FontAwesome imports. */
const iconMap: Record<string, IconDefinition> = {
  "book-open": faBookOpen,
  calendar: faCalendar,
  video: faVideo,
  users: faUsers,
  file: faFile,
  "wand-magic-sparkles": faWandMagicSparkles,
  "user-headset": faUserHeadset,
  "mobile-screen": faMobileScreen,
  "money-bill-1": faMoneyBill1,
  megaphone: faMegaphone,
  shield: faShield,
  link: faLink,
  globe: faGlobe,
  infinity: faInfinity,
  brain: faBrain,
  "circle-play": faCirclePlay,
  "screen-users": faScreenUsers,
  browsers: faBrowsers,
};

/**
 * NavbarV2 — sticky navbar for the Home V2 page.
 *
 * Visual states:
 *   • At top of page (no scroll): fully transparent so the page-wide
 *     purple gradient wash (declared on the HomeV2 wrapper) reads through
 *     the navbar as one continuous background with the hero.
 *   • After the user scrolls past SCROLL_THRESHOLD: glass effect kicks in
 *     — translucent white background with a backdrop blur — so the nav
 *     stays readable over content beneath it.
 *
 * Layout:
 *   • GRAPHY wordmark on the left.
 *   • Products / Resources / Pricing menu items in the center.
 *   • Login (tertiary / Link) + Sign Up (Secondary, matches the "Start
 *     Your Free Trial" button in the hero) on the right.
 *
 * Dropdowns:
 *   • "Products" and "Resources" open a mega-menu panel on hover/focus.
 *     The panel is anchored to the sticky header (`position: absolute`
 *     just under the nav row) and uses a brief close-delay so moving the
 *     mouse from the trigger onto the panel doesn't dismiss it.
 */

const ChevronDown = ({ open }: { open: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className={classNames(
      "transition-transform duration-200",
      open ? "rotate-180" : "rotate-0"
    )}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const NewBadge = () => (
  <span className="ml-2 inline-flex items-center rounded-full bg-[#CCFF00] px-2 py-[2px] font-inter text-[10px] font-semibold uppercase tracking-wider text-[#0C0C0C]">
    New
  </span>
);

const ComingSoonBadge = () => (
  <span className="ml-2 inline-flex items-center rounded-full bg-[#EDE9FF] px-2 py-[2px] font-inter text-[10px] font-semibold text-brandPrimary">
    Coming soon
  </span>
);

/** Single row inside a dropdown column — icon + title + description.
 *  Rows without `href` render as non-clickable divs (used for items
 *  whose landing page doesn't exist in the codebase yet). */
const MenuItemRow = ({ item }: { item: NavV2MenuItem }) => {
  const body = (
    <>
      <div className="mt-[2px] shrink-0">
        {item.brand ? (
          <span
            className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full font-inter text-[14px] font-semibold"
            style={{ backgroundColor: item.brand.bg, color: item.brand.color ?? "#0C0C0C" }}
            aria-hidden="true"
          >
            {item.brand.image ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={item.brand.image} alt="" className="h-full w-full object-contain" />
            ) : (
              item.brand.letter
            )}
          </span>
        ) : item.icon && iconMap[item.icon] ? (
          // Icon color matches the subtext color (darkSlate02) per the
          // Figma spec — secondary/muted treatment rather than full-black.
          <span
            className="flex h-8 w-8 items-center justify-center text-darkSlate02"
            aria-hidden="true"
          >
            <FontAwesomeIcon
              icon={iconMap[item.icon]}
              style={{ fontSize: 20 }}
            />
          </span>
        ) : null}
      </div>
      <div className="min-w-0">
        <div className="flex items-center">
          <span className="font-inter text-[15px] font-semibold text-neutralBlack">
            {item.name}
          </span>
          {item.badge === "new" ? <NewBadge /> : null}
          {item.badge === "coming-soon" ? <ComingSoonBadge /> : null}
        </div>
        <p className="mt-1 font-inter text-[13px] leading-snug text-darkSlate02">
          {item.description}
        </p>
      </div>
    </>
  );

  const rowClass =
    "group flex items-start gap-3 rounded-xl p-2 transition-colors";

  if (item.href) {
    return (
      <Link href={item.href} className={classNames(rowClass, "hover:bg-slateLight")}>
        {body}
      </Link>
    );
  }
  return <div className={rowClass}>{body}</div>;
};

const MenuColumn = ({ column }: { column: NavV2MenuColumn }) => (
  <div>
    {/* Column heading — Inter Medium, neutral dark. Deliberately not
        uppercase/tracking-wider so it reads as a label rather than an
        all-caps eyebrow. */}
    <div className="px-2 pb-4 font-inter text-[14px] font-medium text-darkSlate02">
      {column.heading}
    </div>
    <div className="flex flex-col gap-1">
      {column.items.map((item) => (
        <MenuItemRow key={item.name} item={item} />
      ))}
    </div>
  </div>
);

const SCROLL_THRESHOLD = 8;
const SCROLLED_CLASSES = [
  "bg-white/60",
  "backdrop-blur-md",
  "border-white/40",
];
const RESTING_CLASSES = ["border-transparent"];

/** Close-delay (ms) after the cursor leaves a trigger/panel. Small grace
 *  period so diagonal cursor moves between the button and the panel
 *  don't close the dropdown. */
const CLOSE_DELAY_MS = 120;

const NavbarV2 = () => {
  const headerRef = useRef<HTMLElement | null>(null);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scroll handler writes directly to the header's classList. Bypassing
  // React state proved more reliable than setState-based approaches here —
  // the setState version wasn't picking up programmatic scrolls in some
  // environments (preview tooling, route transitions). requestAnimationFrame
  // keeps it cheap.
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;
    let ticking = false;
    const apply = () => {
      ticking = false;
      const scrolled = window.scrollY > SCROLL_THRESHOLD;
      if (scrolled) {
        header.classList.add(...SCROLLED_CLASSES);
        header.classList.remove(...RESTING_CLASSES);
      } else {
        header.classList.remove(...SCROLLED_CLASSES);
        header.classList.add(...RESTING_CLASSES);
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close any open menu on Escape for keyboard users.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Lock body scroll when the mobile drawer is open.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openWithDelay = (name: string) => {
    clearCloseTimer();
    setOpenMenu(name);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => setOpenMenu(null), CLOSE_DELAY_MS);
  };

  const activeItem: NavV2Item | undefined = navbarV2Data.items.find(
    (i) => i.type === "menu" && i.name === openMenu
  );

  return (
    <header
      ref={headerRef}
      className={classNames(
        "sticky top-0 z-bar w-full border-b border-transparent font-inter transition-[background-color,backdrop-filter,border-color] duration-200",
        // When the mobile drawer is open, force a solid white bg so the
        // page-wide purple/yellow gradient wash doesn't read through the
        // expanded header.
        mobileOpen ? "!bg-white !backdrop-blur-0" : ""
      )}
    >
      {/* Horizontal padding mirrors the section containers below —
          px-4 → sm:px-6 → md:px-8 → lg:px-12 — so the navbar anchors
          visually with the page. */}
      <div className="container relative mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
        <nav className="relative mx-auto flex max-w-6xl items-center justify-between py-4 md:py-5">
          {/* Mobile burger — left side on mobile, hidden on desktop. */}
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center text-neutralBlack md:hidden"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => {
              setMobileOpen((v) => !v);
              setMobileAccordion(null);
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              aria-hidden="true"
            >
              {mobileOpen ? (
                <>
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </>
              ) : (
                <>
                  <line x1="3" y1="7" x2="21" y2="7" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="17" x2="21" y2="17" />
                </>
              )}
            </svg>
          </button>

          {/* Wordmark — absolutely centered on mobile, left-anchored on desktop. */}
          <Link
            href="/"
            aria-label="Graphy home"
            className="absolute left-1/2 -translate-x-1/2 shrink-0 md:static md:translate-x-0"
          >
            <Image
              src="/images/graphy_logo_black.svg"
              width={110}
              height={32}
              alt="Graphy"
              priority
              className="h-7 w-auto md:h-8"
            />
          </Link>

          {/* Center: menu items */}
          <ul className="hidden items-center gap-10 md:flex">
            {navbarV2Data.items.map((item) => {
              if (item.type === "link") {
                return (
                  <li key={item.name}>
                    <Link
                      href={item.href}
                      className="font-inter text-[16px] font-medium text-neutralBlack hover:text-sapphire02"
                    >
                      {item.name}
                    </Link>
                  </li>
                );
              }
              const isOpen = openMenu === item.name;
              return (
                <li
                  key={item.name}
                  onMouseEnter={() => openWithDelay(item.name)}
                  onMouseLeave={scheduleClose}
                  onFocus={() => openWithDelay(item.name)}
                  onBlur={scheduleClose}
                >
                  <button
                    type="button"
                    className={classNames(
                      "flex items-center gap-1.5 font-inter text-[16px] font-medium transition-colors",
                      isOpen
                        ? "text-brandPrimary"
                        : "text-neutralBlack hover:text-sapphire02"
                    )}
                    aria-expanded={isOpen}
                    onClick={() =>
                      setOpenMenu((cur) => (cur === item.name ? null : item.name))
                    }
                  >
                    <span>{item.name}</span>
                    <ChevronDown open={isOpen} />
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Right: Login (tertiary, desktop only) + Sign Up (secondary) */}
          <div className="flex items-center gap-4 md:gap-5">
            <Link
              href="/signin"
              className="hidden font-inter text-[16px] font-medium text-neutralBlack hover:text-sapphire02 md:inline-flex"
            >
              Login
            </Link>
            <Button
              type={ButtonTypeEnum.Secondary}
              size={ButtonSizeEnum.Small}
              href="/signup"
            >
              <span className="font-inter text-[14px] font-medium md:text-[15px]">
                Sign Up
              </span>
            </Button>
          </div>
        </nav>

        {/* Mobile drawer — accordions for menu items, pricing link, login button.
            Rendered only on mobile (`md:hidden`). Slides/fades in below the nav
            row and locks body scroll while open. */}
        {mobileOpen ? (
          <div className="absolute left-0 right-0 top-full z-[60] md:hidden">
            <div className="max-h-[calc(100vh-64px)] overflow-y-auto bg-white px-4 pb-6 pt-2 shadow-[0_24px_60px_-20px_rgba(17,17,17,0.18)] sm:px-6">
              <ul className="flex flex-col">
                {navbarV2Data.items.map((item) => {
                  if (item.type === "link") {
                    return (
                      <li key={item.name} className="">
                        <Link
                          href={item.href}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center justify-between py-4 font-inter text-[16px] font-medium text-neutralBlack"
                        >
                          {item.name}
                        </Link>
                      </li>
                    );
                  }
                  const isAccOpen = mobileAccordion === item.name;
                  return (
                    <li key={item.name} className="">
                      <button
                        type="button"
                        className="flex w-full items-center justify-between py-4 font-inter text-[16px] font-medium text-neutralBlack"
                        aria-expanded={isAccOpen}
                        onClick={() =>
                          setMobileAccordion((cur) =>
                            cur === item.name ? null : item.name
                          )
                        }
                      >
                        <span>{item.name}</span>
                        <ChevronDown open={isAccOpen} />
                      </button>
                      {isAccOpen ? (
                        <div className="pb-3">
                          {item.columns.map((col) => (
                            <div key={col.heading} className="pb-2">
                              <div className="px-2 pb-2 pt-1 font-inter text-[13px] font-medium text-darkSlate02">
                                {col.heading}
                              </div>
                              <div className="flex flex-col gap-1">
                                {col.items.map((sub) => (
                                  <div
                                    key={sub.name}
                                    onClick={() => {
                                      if (sub.href) setMobileOpen(false);
                                    }}
                                  >
                                    <MenuItemRow item={sub} />
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-6 w-full">
                <Button
                  type={ButtonTypeEnum.Secondary}
                  size={ButtonSizeEnum.Regular}
                  href="/signin"
                >
                  <span className="font-inter text-[16px] font-medium">
                    Login
                  </span>
                </Button>
              </div>
            </div>
          </div>
        ) : null}

        {/* Dropdown panel — one panel, content swapped based on which
            trigger is open. Positioned just below the nav row; keeping
            it inside the same relative container auto-aligns with the
            left/right padding of the navbar. */}
        {activeItem && activeItem.type === "menu" ? (
          <div
            className="absolute left-0 right-0 top-full z-[60] hidden px-4 sm:px-6 md:block md:px-8 lg:px-12"
            onMouseEnter={() => openWithDelay(activeItem.name)}
            onMouseLeave={scheduleClose}
          >
            <div className="mx-auto mt-3 max-w-6xl overflow-hidden rounded-3xl border border-slateStroke bg-white p-8 shadow-[0_24px_60px_-20px_rgba(17,17,17,0.18)]">
              <div
                className={classNames(
                  "grid gap-10",
                  activeItem.columns.length === 3
                    ? "md:grid-cols-3"
                    : "md:grid-cols-2"
                )}
              >
                {activeItem.columns.map((col) => (
                  <MenuColumn key={col.heading} column={col} />
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </header>
  );
};

export default NavbarV2;
